import { Directive, HostListener, inject, input } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[pwjOnlyNumber]',
  standalone: true,
})
export class OnlyNumberDirective {
  // Optional input property with a default value
  isFloatNumber = input<boolean>();
  // dependencies
  private readonly control = inject(NgControl);
  constructor() {}

  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    const targetElement = e.target as HTMLInputElement;

    if (
      // Allow: Delete, Backspace, Tab, Escape, Enter, etc
      ['Delete', 'Backspace', 'Tab', 'Escape', 'Enter'].includes(e.key) ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.key === 'a' && e.ctrlKey) ||
      (e.key === 'c' && e.ctrlKey) ||
      (e.key === 'v' && e.ctrlKey) ||
      (e.key === 'x' && e.ctrlKey)
    ) {
      return; // let it happen, don't do anything
    }

    // Allow a single '.' only if it doesn't already exist in the input value
    if (this.isFloatNumber() && e.key === '.') {
      if (targetElement.value.includes('.')) {
        e.preventDefault(); // Prevent adding a second '.'
      }
      return; // Allow the first '.'
    }
    // Ensure that it is a number and stop the keypress
    if (e.key === ' ' || isNaN(Number(e.key))) {
      e.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    if (event.clipboardData) {
      const pastedInput: string = event.clipboardData
        .getData('text/plain')
        .replace(/\D/g, ''); // get a digit-only string

      // Use the event target to directly manipulate the input field's value
      const target = event.target as HTMLInputElement;

      if (document.activeElement === target) {
        const start = target.selectionStart || 0;
        const end = target.selectionEnd || 0;

        // Insert the pasted input at the current cursor position
        const value = target.value;
        target.value =
          value.substring(0, start) + pastedInput + value.substring(end);

        // Move the cursor to the end of the pasted text
        target.setSelectionRange(
          start + pastedInput.length,
          start + pastedInput.length
        );
      }
      // Trigger form control update if NgControl is present
      if (this.control) {
        this.control.control?.setValue(target.value);
        this.control.control?.markAsTouched();
        this.control.control?.updateValueAndValidity();
      } else {
        // If not using Angular forms, manually trigger validity
        target.dispatchEvent(new Event('input')); // Trigger input event for native change detection
        target.setCustomValidity('');
        target.checkValidity(); // Re-check validity after update
      }
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      const textData = event.dataTransfer.getData('text').replace(/\D/g, '');
      const selection = window.getSelection();
      if (!selection?.rangeCount) return;

      const range = selection.getRangeAt(0);
      const newNode = document.createTextNode(textData);
      range.deleteContents();
      range.insertNode(newNode);
    }
  }
}
